import { useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { usePageAccessGuard } from "@/hooks/usePageAccessGuard";
import { useSimplifiedUserData } from "@/providers/SimplifiedUserDataProvider";
import { UnifiedItemsContainer } from "@/components/layout/page-containers";
import { PageHandlers, TransformContext } from "@/components/layout/page-containers/UnifiedItemsContainer/types";
import { blueprintsConfig } from "./unified-items/configs/blueprintsConfig";
import { CardData } from "@/components/layout/element-layout/grid-layout/GridLayout";

// Import stores and types
import {
  templatesStore,
  createTemplate,
  updateTemplate,
  deleteTemplate,
  fetchTemplates,
} from "@/lib/stores/templatesStore";
import { GlossaryBlueprint } from "@/types-games";
import { templatesDataSource } from "@/lib/dataSources/sources/templates";
import { BlueprintData } from "@/components/wizards/blueprints/types";
import { Version } from "@/lib/stores/storeTypes";
import GlossaryWidget from "@/components/cards/widgets/ObjectsWidget";

/**
 * BluePrintsPage - SOURCE OF TRUTH for template/blueprint operations
 * 
 * This page owns all blueprint-specific:
 * - Store operations (write/update/delete in templatesStore)
 * - CRUD handlers (create, update, delete templates)
 * - Card transformation (raw GlossaryBlueprint → CardData)
 * - Wizard callbacks (what happens after create/update)
 * 
 * When the structure of templates changes, update THIS file.
 */
export default function BluePrintsPage() {
  usePageAccessGuard('templates');
  
  const { toast } = useToast();
  const navigate = useNavigate();
  const { selectedGame } = useSimplifiedUserData();

  // #region Store Operations
  /**
   * Blueprint-specific store operations
   * These define how data is written to/from the templatesStore
   */
  const storeOperations = useMemo(() => ({
    writeToStore: (items: GlossaryBlueprint[]) => {
      templatesStore.setData(items);
    },
    updateInStore: (id: string, data: Partial<GlossaryBlueprint>) => {
      const current = templatesStore.getData();
      const updated = current.map(item => 
        item.id === id ? { ...item, ...data } : item
      );
      templatesStore.setData(updated);
    },
    deleteFromStore: (id: string) => {
      const current = templatesStore.getData();
      templatesStore.setData(current.filter(item => item.id !== id));
    },
  }), []);
  // #endregion

  // #region CRUD Handlers
  /**
   * Blueprint-specific CRUD handlers
   * These handle server communication for templates
   */
  const handlers = useMemo(() => ({
    onCreate: async (data: any, folderId?: string): Promise<GlossaryBlueprint> => {
      if (!selectedGame?.id) {
        throw new Error("No game selected");
      }

      const templateData = {
        game_id: data.game_id || selectedGame.id,
        name: data.name,
        tags: data.tags || [],
        type: data.type || "default",
        description: data.description || "",
        data: data.data as unknown as BlueprintData,
        settings: data.settings || {},
        version: data.version,
        folder: folderId || data.folder || null,
      };

      const createdTemplate = await createTemplate(templateData);
      
      // Update local store
      const current = templatesStore.getData();
      templatesStore.setData([...current, createdTemplate]);
      
      return createdTemplate;
    },

    onUpdate: async (id: string, data: Partial<GlossaryBlueprint>): Promise<GlossaryBlueprint> => {
      if (!selectedGame?.id) {
        throw new Error("No game selected");
      }

      const existingTemplate = templatesStore.getData().find(item => item.id === id);
      if (!existingTemplate) {
        throw new Error("Template not found");
      }

      const templateData = {
        id,
        game_id: data.game_id || selectedGame.id,
        name: data.name || existingTemplate.name,
        tags: data.tags || existingTemplate.tags || [],
        type: data.type || existingTemplate.type || "default",
        description: data.description || existingTemplate.description || "",
        data: (data.data || existingTemplate.data) as unknown as BlueprintData,
        settings: data.settings || existingTemplate.settings || {},
        version: data.version || existingTemplate.version,
        folder: data.folder !== undefined ? data.folder : existingTemplate.folder,
      };

      const updatedTemplate = await updateTemplate(templateData);
      
      // Update local store
      storeOperations.updateInStore(id, updatedTemplate);
      
      return updatedTemplate;
    },

    onDelete: async (id: string): Promise<void> => {
      if (!selectedGame?.id) {
        throw new Error("No game selected");
      }

      await deleteTemplate(id, selectedGame.id, toast);
      storeOperations.deleteFromStore(id);
    },

    onBulkDelete: async (ids: string[]): Promise<void> => {
      if (!selectedGame?.id) {
        throw new Error("No game selected");
      }

      for (const id of ids) {
        await deleteTemplate(id, selectedGame.id, toast);
        storeOperations.deleteFromStore(id);
      }
    },
  }), [selectedGame, toast, storeOperations]);
  // #endregion

  // #region Card Transformation
  /**
   * Transform blueprint items to CardData for display
   * Uses the DataSource transformer for consistency
   */
  const transformToCard = useCallback((item: GlossaryBlueprint, context: TransformContext): CardData => {
    // Use DataSource transformer with context for single source of truth
    const cards = templatesDataSource.transformers.toCard?.([item], {
      tags: context.tags,
      getUserNameById: context.getUserNameById,
    }) || [];
    
    return cards[0] || {
      id: item.id,
      title: item.name,
      gameId: item.game_id,
      folder: item.folder,
    };
  }, []);

  /**
   * Render a blueprint card using GlossaryWidget
   */
  const renderCard = useCallback((card: CardData, tags: any[]) => {
    return (
      <GlossaryWidget
        id={card.id}
        gameId={card.gameId}
        title={card.title}
        tags={card.tags}
        description={card.description}
        lastUpdate={card.lastUpdate}
        version={card.version as number | Version}
        status={card.status}
        folder={card.folder as string}
        itemType="templates"
        icon={card.icon}
        iconBg={card.iconBg}
        shadowType={card.shadowType}
        shadowColor={card.shadowColor}
        hasVersionMismatch={card.hasVersionMismatch}
        owner={card.owner}
        onDelete={card.onDelete}
        onEdit={card.onEdit}
      />
    );
  }, []);
  // #endregion

  // #region Edit Handler
  /**
   * Handle edit action for templates - navigate to detail page
   */
  const onEdit = useCallback((id: string, data: GlossaryBlueprint[], navigateFn: (path: string) => void) => {
    // Navigate to template detail page
    const template = data.find(item => item.id === id);
    navigate(`/templates/${id}`, { state: { itemName: template?.name } });
  }, [navigate]);
  // #endregion

  // #region Wizard Callbacks
  /**
   * Blueprint-specific wizard callbacks
   * Define what happens after wizard create/update operations
   */
  const wizardCallbacks = useMemo(() => ({
    onItemCreated: async (item: GlossaryBlueprint) => {
      toast({
        title: "Success",
        description: `Template "${item.name}" created successfully`,
      });
      
      // Refresh data from server to ensure sync
      if (selectedGame?.id) {
        try {
          await fetchTemplates(selectedGame.id);
        } catch (error) {
          console.warn("Failed to refresh templates after creation:", error);
        }
      }
    },

    onItemUpdated: async (item: GlossaryBlueprint) => {
      toast({
        title: "Success",
        description: `Template "${item.name}" updated successfully`,
      });
      
      // Refresh data from server to ensure sync
      if (selectedGame?.id) {
        try {
          await fetchTemplates(selectedGame.id);
        } catch (error) {
          console.warn("Failed to refresh templates after update:", error);
        }
      }
    },

    onWizardClose: () => {
      // Any cleanup needed when wizard closes
    },
  }), [toast, selectedGame]);
  // #endregion

  // #region Combined PageHandlers
  /**
   * Combine all handlers into PageHandlers interface
   */
  const pageHandlers: PageHandlers<GlossaryBlueprint> = useMemo(() => ({
    storeOperations,
    handlers,
    transformToCard,
    renderCard,
    wizardCallbacks,
    onEdit,
    features: {
      supportsPendingChanges: true, // Templates support pending changes
      supportsBulkCreate: false,
      supportsFlowView: false,
    },
  }), [storeOperations, handlers, transformToCard, renderCard, wizardCallbacks, onEdit]);
  // #endregion

  return (
    <UnifiedItemsContainer 
      itemType="blueprints"
      handlers={pageHandlers}
      config={blueprintsConfig}
    />
  );
}
